/**
 * What the rundown tree shows, as plain view models: the materialised tree
 * (`@acp/domain/tree.js`) flattened into the visible rows of a virtualised
 * list, Segment → Part → Piece, each row reduced to the fields its line
 * displays. Components get these, never a `Row` — they do not learn the
 * replication model (the same rule as `RundownSummary`).
 *
 * Adlibs are not shown (they belong to the playout view, D25). Rows the
 * materialiser put in "Lost & found" (orphans, cut cycles — PLAN §6.2) are
 * listed after the rundown under a header, so nothing a user wrote is ever
 * silently invisible.
 */
import { readFields } from '@acp/domain/fields.js';
import type { Row } from '@acp/domain/row.js';
import { childrenOf, LOST_AND_FOUND_KEY, type RowTree } from '@acp/domain/tree.js';
import { pieceTypeShort } from './pieceTypes.js';

export type TreeKind = 'segment' | 'part' | 'piece';

export type SegmentView = {
  readonly kind: 'segment';
  readonly name: string;
  readonly budgetDuration: number | null;
  readonly hidden: boolean;
  /** Sum of the expected durations of its timed parts (untimed ones are not counted, PLAN §5.1). */
  readonly totalDuration: number;
  readonly partCount: number;
};

export type PartView = {
  readonly kind: 'part';
  readonly title: string;
  readonly expectedDuration: number | null;
  readonly autoNext: boolean;
  readonly untimed: boolean;
  readonly floated: boolean;
  readonly invalid: string | null;
  readonly pieceCount: number;
  readonly hasScript: boolean;
};

export type PieceView = {
  readonly kind: 'piece';
  readonly name: string;
  readonly pieceType: string;
  readonly typeShort: string;
  readonly sourceLayer: string;
  readonly start: number | 'now';
  readonly duration: number | null;
};

export type RowView = SegmentView | PartView | PieceView;

export type FlatRow = {
  readonly id: string;
  readonly parentId: string | null;
  readonly depth: 0 | 1 | 2;
  readonly view: RowView;
  readonly hasChildren: boolean;
  readonly collapsed: boolean;
  /** Under the "Lost & found" header, not the rundown. */
  readonly lost: boolean;
};

export type FlatTree = { readonly rows: readonly FlatRow[]; readonly lostCount: number };

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** A row's fields in their typed view; a row whose stored data fails its schema is shown with what can be read. */
function fieldsOf(row: Row): Readonly<Record<string, unknown>> {
  const read = readFields(row.kind, row.f);
  return read.ok ? read.value.fields : row.f;
}

function numOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function invalidReason(row: Row, f: Readonly<Record<string, unknown>>): string | null {
  const read = readFields(row.kind, row.f);
  if (!read.ok) return read.error;
  if (f['invalid'] !== true) return null;
  const reason = str(f['invalidReason']);
  return reason === '' ? 'invalid' : reason;
}

export function partView(row: Row, pieceCount: number): PartView {
  const f = fieldsOf(row);
  return {
    kind: 'part',
    title: str(f['title']),
    expectedDuration: numOrNull(f['expectedDuration']),
    autoNext: f['autoNext'] === true,
    untimed: f['untimed'] === true,
    floated: f['floated'] === true,
    invalid: invalidReason(row, f),
    pieceCount,
    hasScript: str(f['script']).trim() !== '',
  };
}

export function pieceView(row: Row): PieceView {
  const f = fieldsOf(row);
  const enable = f['enable'];
  const start = typeof enable === 'object' && enable !== null ? Reflect.get(enable, 'start') : 0;
  const duration = typeof enable === 'object' && enable !== null ? Reflect.get(enable, 'duration') : null;
  const pieceType = str(f['pieceType']);
  return {
    kind: 'piece',
    name: str(f['name']),
    pieceType,
    typeShort: pieceTypeShort(pieceType),
    sourceLayer: str(f['sourceLayer']),
    start: start === 'now' ? 'now' : typeof start === 'number' ? start : 0,
    duration: numOrNull(duration),
  };
}

export function segmentView(row: Row, parts: readonly PartView[]): SegmentView {
  const f = fieldsOf(row);
  return {
    kind: 'segment',
    name: str(f['name']),
    budgetDuration: numOrNull(f['budgetDuration']),
    hidden: f['hidden'] === true,
    totalDuration: parts.reduce((sum, p) => sum + (p.untimed ? 0 : (p.expectedDuration ?? 0)), 0),
    partCount: parts.length,
  };
}

function isTreeKind(kind: string): kind is TreeKind {
  return kind === 'segment' || kind === 'part' || kind === 'piece';
}

type Emit = (row: FlatRow) => void;

function emitPart(tree: RowTree, part: Row, lost: boolean, collapsed: ReadonlySet<string>, emit: Emit, depth: 0 | 1): PartView {
  const pieces = childrenOf(tree, part.rowId).filter((r) => r.kind === 'piece');
  const view = partView(part, pieces.length);
  const isCollapsed = collapsed.has(part.rowId);
  emit({ id: part.rowId, parentId: part.parentId, depth, view, hasChildren: pieces.length > 0, collapsed: isCollapsed, lost });
  if (!isCollapsed) {
    for (const piece of pieces) {
      emit({ id: piece.rowId, parentId: piece.parentId, depth: depth === 0 ? 1 : 2, view: pieceView(piece), hasChildren: false, collapsed: false, lost });
    }
  }
  return view;
}

function emitSegment(tree: RowTree, segment: Row, lost: boolean, collapsed: ReadonlySet<string>, emit: Emit): void {
  const isCollapsed = collapsed.has(segment.rowId);
  const parts = childrenOf(tree, segment.rowId).filter((r) => r.kind === 'part');
  const buffered: FlatRow[] = [];
  const partViews = parts.map((p) => emitPart(tree, p, lost, collapsed, (r) => buffered.push(r), 1));
  emit({ id: segment.rowId, parentId: segment.parentId, depth: 0, view: segmentView(segment, partViews), hasChildren: parts.length > 0, collapsed: isCollapsed, lost });
  if (!isCollapsed) for (const r of buffered) emit(r);
}

/**
 * The visible rows, in display order: the rundown's segments (its direct
 * children in the tree), each followed by its parts and their pieces unless
 * collapsed; then the lost & found rows at their own kinds' depths.
 */
export function flattenTree(tree: RowTree, rundownId: string, collapsed: ReadonlySet<string>): FlatTree {
  const rows: FlatRow[] = [];
  const emit: Emit = (r) => rows.push(r);
  for (const segment of childrenOf(tree, rundownId)) {
    if (segment.kind === 'segment') emitSegment(tree, segment, false, collapsed, emit);
  }
  const lost = childrenOf(tree, LOST_AND_FOUND_KEY).filter((r) => isTreeKind(r.kind));
  for (const row of lost) {
    if (row.kind === 'segment') emitSegment(tree, row, true, collapsed, emit);
    else if (row.kind === 'part') emitPart(tree, row, true, collapsed, emit, 0);
    else emit({ id: row.rowId, parentId: row.parentId, depth: 0, view: pieceView(row), hasChildren: false, collapsed: false, lost: true });
  }
  return { rows, lostCount: lost.length };
}

/** The name a row goes by in a toast or a confirmation ("Delete “Weather”?"). */
export function displayName(view: RowView): string {
  const name = view.kind === 'part' ? view.title : view.name;
  return name === '' ? `(untitled ${view.kind})` : name;
}
